import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { PlusIcon } from '@heroicons/react/24/outline';
import { obtenerArmazonesDisponibles, agregarArmazon, obtenerCatalogosCristales, obtenerCatalogosArmazones } from '../api/backend_v2';
import { calcularPrecioEfectivoConDescuento, cargarDescuentoEfectivoSeguro, DESCUENTO_EFECTIVO_DEFAULT } from '../utils/precios';
import { obtenerProveedores } from '../utils/proveedores';
import NuevoProveedorRapidoModal from './NuevoProveedorRapidoModal';

const ArmazonSearchInput = ({ 
  name, 
  value, 
  onChange, 
  placeholder = "Buscar armazón...",
  required = false,
  onArmazonSelect = null,   // callback(armazon) — expone el objeto completo (con id) al padre
}) => {
  const [armazonesDisponibles, setArmazonesDisponibles] = useState([]);
  const [filteredArmazones, setFilteredArmazones] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [armazonSeleccionado, setArmazonSeleccionado] = useState(false);
  const [descuentoEfectivo, setDescuentoEfectivo] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [precioEfectivoManual, setPrecioEfectivoManual] = useState(false);
  const [marcasCatalogo, setMarcasCatalogo] = useState([]);
  const [marcasCatalogoObjetos, setMarcasCatalogoObjetos] = useState([]);
  const [materialesArmazonesCatalogo, setMaterialesArmazonesCatalogo] = useState([]);
  const [coloresArmazonesCatalogo, setColoresArmazonesCatalogo] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [newArmazonForm, setNewArmazonForm] = useState({
    marca: '',
    modelo: '',
    color: '',
    material: 'Acetato',
    cantidad: 1,
    medidas: '',
    tipo_bisagra: 'Estándar',
    costo: '',
    precio_publico: '',
    precio_efectivo: '',
    id_proveedor: ''
  });
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const modalAbiertoRef = useRef(false);

  // Cargar armazones disponibles al montar el componente
  useEffect(() => {
    cargarArmazones();
    cargarCatalogoMarcas();
    cargarCatalogosArmazones();
    cargarProveedores();
  }, []);

  const cargarCatalogosArmazones = async () => {
    try {
      const res = await obtenerCatalogosArmazones(false);
      setMaterialesArmazonesCatalogo(res.material_armazon || []);
      setColoresArmazonesCatalogo(res.color_armazon || []);
    } catch {
      setMaterialesArmazonesCatalogo([]);
      setColoresArmazonesCatalogo([]);
    }
  };

  const cargarProveedores = async () => {
    try {
      const data = await obtenerProveedores();
      setProveedores(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setProveedores([]);
    }
  };

  useEffect(() => {
    cargarDescuentoEfectivoSeguro().then(setDescuentoEfectivo);
  }, []);

  const calcularPrecioEfectivo = (precioPublico) => {
    return calcularPrecioEfectivoConDescuento(precioPublico, descuentoEfectivo);
  };

  const cargarCatalogoMarcas = async () => {
    try {
      const response = await obtenerCatalogosCristales(false);
      const activas = (response?.marcas_armazones || []).filter((item) => item?.activo);
      setMarcasCatalogoObjetos(activas);
      const marcas = activas
        .map((item) => String(item.nombre || '').trim())
        .filter(Boolean);
      setMarcasCatalogo(marcas);
    } catch (error) {
      console.error('Error al cargar catálogo de marcas de armazones:', error);
      setMarcasCatalogo([]);
      setMarcasCatalogoObjetos([]);
    }
  };

  // Actualizar valor del input cuando cambie la prop value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const cargarArmazones = async () => {
    setLoading(true);
    try {
      const response = await obtenerArmazonesDisponibles();
      setArmazonesDisponibles(response.armazones || []);
    } catch (error) {
      console.error('Error al cargar armazones:', error);
      setArmazonesDisponibles([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar armazones basado en el input
  useEffect(() => {
    if (inputValue.trim() === '') {
      setFilteredArmazones(armazonesDisponibles);
    } else {
      const searchTerm = inputValue.toLowerCase();
      const filtered = armazonesDisponibles.filter(armazon =>
        armazon.marca.toLowerCase().includes(searchTerm) ||
        armazon.modelo.toLowerCase().includes(searchTerm) ||
        armazon.color.toLowerCase().includes(searchTerm) ||
        `${armazon.marca} ${armazon.modelo}`.toLowerCase().includes(searchTerm) ||
        `${armazon.marca} ${armazon.modelo} ${armazon.color}`.toLowerCase().includes(searchTerm)
      );
      setFilteredArmazones(filtered);
    }
  }, [inputValue, armazonesDisponibles]);

  // Manejar cambios en el input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowDropdown(true);
    setArmazonSeleccionado(false);  // Resetear flag cuando el usuario escribe
    
    // Notificar al componente padre
    onChange({ target: { name, value: newValue } });
  };

  // Seleccionar armazón del dropdown
  const selectArmazon = (armazon) => {
    const displayValue = `${armazon.marca} ${armazon.modelo} - ${armazon.color}`;
    setInputValue(displayValue);
    setShowDropdown(false);
    setArmazonSeleccionado(true);  // Marcar que se seleccionó un armazón válido
    onChange({ target: { name, value: displayValue } });
    onArmazonSelect?.(armazon);  // expone {id, marca, modelo, color, ...} al padre
  };

  // Manejar blur del input (sin pop-ups automáticos)
  const handleBlur = (e) => {
    // Delay para permitir clicks en el dropdown o en los botones
    setTimeout(() => {
      if (modalAbiertoRef.current || Swal.isVisible()) return;
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
      }
    }, 200);
  };

  // Abrir modal para agregar armazón nuevo (invocado voluntariamente por el usuario mediante botón)
  const abrirModalAgregarArmazon = (cleanValue = '') => {
    if (modalAbiertoRef.current) return;
    const cleanInputValue = (cleanValue || inputValue || '').trim();
    
    // Intentar parsear el input para prellenar el formulario
    const parts = cleanInputValue.split(/[-\s]+/);
    let marcaInicial = '';
    let modeloInicial = '';
    let colorInicial = '';
    
    if (parts.length >= 3) {
      marcaInicial = parts[0].trim();
      modeloInicial = parts[1].trim();
      colorInicial = parts.slice(2).join(' ').trim();
    } else if (parts.length === 2) {
      marcaInicial = parts[0].trim();
      modeloInicial = parts[1].trim();
    } else {
      marcaInicial = cleanInputValue;
    }
    
    setNewArmazonForm(prev => ({
      ...prev,
      marca: marcaInicial,
      modelo: modeloInicial,
      color: colorInicial
    }));
    modalAbiertoRef.current = true;
    setShowModal(true);
  };

  // Manejar cambios en el formulario del modal
  const handleModalFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setNewArmazonForm(prev => {
      const next = {
        ...prev,
        [name]: nextValue,
      };

      if (name === 'precio_publico' && !precioEfectivoManual) {
        next.precio_efectivo = calcularPrecioEfectivo(nextValue);
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManual(String(value || '').trim() !== '');
    }
    if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManual) {
      setNewArmazonForm(prev => ({ ...prev, precio_efectivo: '' }));
    }
  };

  // Guardar nuevo armazón
  const guardarNuevoArmazon = async () => {
    try {
      // Validar campos requeridos
      if (!newArmazonForm.marca || !newArmazonForm.modelo || !newArmazonForm.color || !newArmazonForm.material) {
        Swal.fire({
          title: 'Error',
          text: 'Los campos Marca, Modelo, Color y Material son obligatorios',
          icon: 'error',
          didOpen: (modal) => {
            modal.style.zIndex = 9999999998;
            modal.parentElement.style.zIndex = 9999999997;
          }
        });
        return;
      }

      const armazonData = {
        ...newArmazonForm,
        cantidad: Number.parseInt(newArmazonForm.cantidad, 10) || 1,
        costo: newArmazonForm.costo ? parseFloat(newArmazonForm.costo) : null,
        precio_publico: newArmazonForm.precio_publico ? parseFloat(newArmazonForm.precio_publico) : null,
        precio_efectivo: newArmazonForm.precio_efectivo ? Number.parseFloat(newArmazonForm.precio_efectivo) : null,
        id_proveedor: newArmazonForm.id_proveedor ? Number.parseInt(newArmazonForm.id_proveedor, 10) : null
      };

      console.log('Enviando datos del armazón:', armazonData);
      const nuevoArmazon = await agregarArmazon(armazonData);
      console.log('Armazón agregado exitosamente');
      
      // Recargar armazones y seleccionar el nuevo
      await cargarArmazones();
      await cargarCatalogoMarcas();
      console.log('Armazones recargados');
      
      // Mostrar en el input el armazón recién creado
      const displayValue = `${newArmazonForm.marca} ${newArmazonForm.modelo} - ${newArmazonForm.color}`;
      setInputValue(displayValue);
      setArmazonSeleccionado(true);
      onChange({ target: { name, value: displayValue } });
      console.log('Input actualizado con:', displayValue);
      // Notificar al padre con el objeto completo
      onArmazonSelect?.(nuevoArmazon?.armazon ?? nuevoArmazon ?? {
        id: null, marca: newArmazonForm.marca, modelo: newArmazonForm.modelo, color: newArmazonForm.color
      });
      
      modalAbiertoRef.current = false;
      setShowModal(false);
      console.log('Modal cerrado');
      
      // Resetear formulario
      setNewArmazonForm({
        marca: '',
        modelo: '',
        color: '',
        material: 'Acetato',
        cantidad: 1,
        medidas: '',
        tipo_bisagra: 'Estándar',
        costo: '',
        precio_publico: '',
        precio_efectivo: '',
        id_proveedor: ''
      });
      setPrecioEfectivoManual(false);
      
      // Mostrar notificación de éxito
      Swal.fire({
        title: '¡Éxito!',
        text: 'Armazón agregado correctamente',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
      
    } catch (error) {
      console.error('Error completo al agregar armazón:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Error al agregar el armazón',
        icon: 'error',
        didOpen: (modal) => {
          modal.style.zIndex = 9999999998;
          modal.parentElement.style.zIndex = 9999999997;
        }
      });
    }
  };

  // Cerrar modal sin guardar
  const cerrarModal = () => {
    modalAbiertoRef.current = false;
    setShowModal(false);
    setNewArmazonForm({
      marca: '',
      modelo: '',
      color: '',
      material: 'Acetato',
      cantidad: 1,
      medidas: '',
      tipo_bisagra: 'Estándar',
      costo: '',
      precio_publico: '',
      precio_efectivo: '',
      id_proveedor: ''
    });
    setPrecioEfectivoManual(false);
  };

  const esArmazonExistente = Boolean(
    armazonSeleccionado ||
    (inputValue.trim() !== '' && armazonesDisponibles.some(armazon => {
      const armazonString = `${armazon.marca} ${armazon.modelo} - ${armazon.color}`.toLowerCase();
      const inputLower = inputValue.trim().toLowerCase();
      return (
        armazonString === inputLower ||
        armazon.marca.toLowerCase() === inputLower ||
        `${armazon.marca} ${armazon.modelo}`.toLowerCase() === inputLower
      );
    }))
  );

  const mostrarBotonGuardarStock = Boolean(inputValue.trim() !== '' && !esArmazonExistente);

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        style={{ width: '100%' }}
        autoComplete="off"
      />
      
      {/* Loading indicator */}
      {loading && (
        <div style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#666'
        }}>
          ⟳
        </div>
      )}

      {/* Dropdown with suggestions */}
      {showDropdown && filteredArmazones.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderTop: 'none',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {filteredArmazones.map((armazon) => (
            <div
              key={armazon.id}
              onClick={() => selectArmazon(armazon)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                opacity: (armazon.cantidad || 0) <= 0 ? 0.6 : 1
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <div style={{ fontWeight: '500', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  {armazon.marca} {armazon.modelo}
                  <span style={{ color: '#666', marginLeft: '5px' }}>- {armazon.color}</span>
                </span>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  backgroundColor: (armazon.cantidad || 0) > 0 ? '#d1fae5' : '#fee2e2',
                  color: (armazon.cantidad || 0) > 0 ? '#059669' : '#dc2626'
                }}>
                  {(armazon.cantidad || 0) > 0 ? `Stock: ${armazon.cantidad}` : 'Sin stock'}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Material: {armazon.material}
                {armazon.medidas && ` | Medidas: ${armazon.medidas}`}
                {armazon.precio_publico != null && ` | $${Math.round(armazon.precio_publico).toLocaleString('es-AR')}`}
                {armazon.precio_efectivo != null && ` (ef: $${Math.round(armazon.precio_efectivo).toLocaleString('es-AR')})`}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sugerencia en dropdown cuando no hay resultados pero se escribió un término no guardado */}
      {showDropdown && filteredArmazones.length === 0 && mostrarBotonGuardarStock && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderTop: 'none',
            padding: '10px 12px',
            zIndex: 1000,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            No encontrado en stock
          </span>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              abrirModalAgregarArmazon(inputValue);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#1d4ed8',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            <PlusIcon style={{ width: '14px', height: '14px' }} />
            Guardar en stock
          </button>
        </div>
      )}

      {/* Botón condicional voluntario cuando no se seleccionó un armazón del stock */}
      {mostrarBotonGuardarStock && !showDropdown && (
        <div style={{
          marginTop: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '2px 4px'
        }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            Armazón manual
          </span>
          <button
            type="button"
            onClick={() => abrirModalAgregarArmazon(inputValue)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#1d4ed8',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dbeafe';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#eff6ff';
            }}
          >
            <PlusIcon style={{ width: '14px', height: '14px' }} />
            Guardar en stock
          </button>
        </div>
      )}

      {/* Modal para agregar nuevo armazón */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100
        }} onClick={(e) => {
          if (e.target === e.currentTarget) {
            cerrarModal();
          }
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                Agregar Nuevo Armazón
              </h2>
              <button
                onClick={cerrarModal}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => { 
              e.preventDefault(); 
              e.stopPropagation();
            }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Marca *:
                  </label>
                  <input
                    type="text"
                    name="marca"
                    value={newArmazonForm.marca}
                    onChange={handleModalFormChange}
                    list={`${name}-marcas-armazones-catalogo`}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                  <datalist id={`${name}-marcas-armazones-catalogo`}>
                    {marcasCatalogo.map((marca) => (
                      <option key={marca} value={marca} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Modelo *:
                  </label>
                  <input
                    type="text"
                    name="modelo"
                    value={newArmazonForm.modelo}
                    onChange={handleModalFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Color *:
                  </label>
                  <select
                    name="color"
                    value={newArmazonForm.color}
                    onChange={handleModalFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="">Seleccionar color...</option>
                    {(coloresArmazonesCatalogo || [])
                      .filter((i) => i.activo)
                      .map((i) => (
                        <option key={i.id} value={i.nombre}>{i.nombre}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Material *:
                  </label>
                  <select
                    name="material"
                    value={newArmazonForm.material}
                    onChange={handleModalFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="">Seleccionar material...</option>
                    {(materialesArmazonesCatalogo || [])
                      .filter((i) => i.activo)
                      .map((i) => (
                        <option key={i.id} value={i.nombre}>{i.nombre}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Cantidad:
                  </label>
                  <input
                    type="number"
                    name="cantidad"
                    value={newArmazonForm.cantidad}
                    onChange={handleModalFormChange}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Tipo de Bisagra:
                  </label>
                  <select
                    name="tipo_bisagra"
                    value={newArmazonForm.tipo_bisagra}
                    onChange={handleModalFormChange}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="Clasica">Clásica</option>
                    <option value="Flex">Flex</option>
                    <option value="Triple">Triple</option>
                    <option value="Silicona">Silicona</option>
                    <option value="Otros">Otros</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Proveedor:
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    name="id_proveedor"
                    value={newArmazonForm.id_proveedor}
                    onChange={handleModalFormChange}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="">-- Seleccionar proveedor (opcional) --</option>
                    {proveedores.map((proveedor) => (
                      <option key={proveedor.id} value={proveedor.id}>
                        {proveedor.nombre}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowProveedorModal(true)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #28a745',
                      borderRadius: '4px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontSize: '14px'
                    }}
                  >
                    + Nuevo
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Medidas (ej: 54-18-145):
                </label>
                <input
                  type="text"
                  name="medidas"
                  value={newArmazonForm.medidas}
                  onChange={handleModalFormChange}
                  placeholder="54-18-145"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Costo:
                  </label>
                  <input
                    type="number"
                    name="costo"
                    value={newArmazonForm.costo}
                    onChange={handleModalFormChange}
                    min="0"
                    step="0.01"
                    placeholder="Costo unitario"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Precio Público:
                  </label>
                  <input
                    type="number"
                    name="precio_publico"
                    value={newArmazonForm.precio_publico}
                    onChange={handleModalFormChange}
                    min="0"
                    step="0.01"
                    placeholder="Precio de venta"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Precio Efectivo:
                  </label>
                  <input
                    type="number"
                    name="precio_efectivo"
                    value={newArmazonForm.precio_efectivo}
                    onChange={handleModalFormChange}
                    min="0"
                    step="0.01"
                    placeholder="Precio en efectivo"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={cerrarModal}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    guardarNuevoArmazon();
                  }}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#3085d6',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Guardar Armazón
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal completo para crear nuevo proveedor desde armazón */}
      <NuevoProveedorRapidoModal
        isOpen={showProveedorModal}
        onClose={() => setShowProveedorModal(false)}
        proveedoresExistentes={proveedores}
        marcaActualNombre={newArmazonForm.marca}
        marcasCatalogo={marcasCatalogoObjetos}
        onProveedorCreado={async (provCreado) => {
          await cargarProveedores();
          if (provCreado?.id) {
            setNewArmazonForm((prev) => ({
              ...prev,
              id_proveedor: String(provCreado.id),
            }));
          }
        }}
      />
    </div>
  );
};

export default ArmazonSearchInput;
